import React from "react";
import * as Mui from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import {
  designColors,
  StatCard,
} from "services/constants/designTokens";

// Same accent/tint mapping as the Approval Center landing page's summary row
// (Approve/index.js's summaryCards) — Pending=orange, Approved=green,
// Rejected=red, Cancelled=neutral slate — kept identical so "Pending" reads
// the same color wherever it shows up in Team Zone. Unlike that landing-page
// row (still static placeholder counts), these are computed live from this
// screen's own leaveData and double as a quick status filter, mirroring
// My Zone > Apply > Leave's LeaveStatusCounts tiles.
const STATUS_CARD_DEFS = [
  {
    key: "Pending",
    label: "Pending",
    icon: AccessTimeIcon,
    accent: "#F97316",
    tint: "#FFF7ED",
  },
  {
    key: "Approved",
    label: "Approved",
    icon: CheckCircleOutlineIcon,
    accent: "#16A34A",
    tint: "#EAFBF1",
  },
  {
    key: "Rejected",
    label: "Rejected",
    icon: HighlightOffIcon,
    accent: "#EF4444",
    tint: "#FEF2F2",
  },
  {
    key: "Cancelled",
    label: "Cancelled",
    icon: RemoveCircleOutlineIcon,
    accent: designColors.slate600,
    tint: designColors.slate100,
  },
];

// Kept separate from STATUS_CARD_DEFS above rather than folded into it —
// that array also backs leaveStatusVisual's real-status → color lookup
// (LeaveDetailPanel's header chip), and "All" isn't a status a leave
// request can actually have, so it has no business in that lookup table.
// Blue matches Admin's "All Users"/"Total Companies" cards so the "this is
// the totals tile" accent reads the same wherever it shows up.
const ALL_CARD_DEF = {
  key: "All",
  label: "All",
  icon: ListAltIcon,
  accent: "#1D4ED8",
  tint: "#EEF4FF",
};

// Bucket the API's `status` string into one of the four canonical statuses
// above (case/spelling tolerant), leaving anything unexpected untouched so
// an unrecognised status never silently disappears from the total.
export const canonicalLeaveStatus = (status) => {
  const key = (status || "").trim().toLowerCase();
  if (key === "approved") return "Approved";
  if (key === "pending") return "Pending";
  if (key === "rejected") return "Rejected";
  if (key === "cancelled" || key === "canceled") return "Cancelled";
  return status;
};

// Single source of truth for a status's accent/tint pair, shared by these
// cards and LeaveDetailPanel's header chip — so "Pending" (etc.) always
// reads the same color whether it's a stat-card tile or the panel chip.
export const leaveStatusVisual = (status) => {
  const canonical = canonicalLeaveStatus(status);
  return (
    STATUS_CARD_DEFS.find((card) => card.key === canonical) || {
      key: canonical,
      accent: designColors.slate600,
      tint: designColors.slate100,
    }
  );
};

// Same auto-fit/minmax technique as Home's WorkforceSummaryCards and the
// Approval Center's own summary row — sizes columns off this row's actual
// rendered width rather than a fixed breakpoint, so 5 cards (All + the 4
// statuses) reflow cleanly instead of a hand-tuned column count fighting
// the sidebar's width the way a fixed md=3/md=2.4 Grid would.
export const LeaveStatusCards = ({ counts, activeFilter, onCardClick }) => {
  const totalCount = Object.values(counts || {}).reduce(
    (sum, count) => sum + (count || 0),
    0,
  );

  return (
    <Mui.Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        "@media (max-width:599.95px)": {
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
        },
        gap: 1.5,
        mb: 2,
      }}
    >
      {[ALL_CARD_DEF, ...STATUS_CARD_DEFS].map((card) => {
        const isAll = card.key === "All";
        return (
          <StatCard
            activeBorder
            key={card.key}
            icon={card.icon}
            title={card.label}
            value={isAll ? totalCount : counts?.[card.key] || 0}
            accent={card.accent}
            tint={card.tint}
            isActive={isAll ? !activeFilter : activeFilter === card.key}
            onClick={onCardClick ? () => onCardClick(card.key) : undefined}
          />
        );
      })}
    </Mui.Box>
  );
};
